import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { MEMBER_EXPERIENCE } from "../gql/queries/eventAccess";
import { ADMIN_EVENTS_CALENDAR } from "../gql/queries/eventsCalendar";
import { ZONE_COLOURS, ZONE_ORDER } from "../helpers/const";
import { evLocal, TIMEZONES, tzLabel, UTC_TZ, zoneLocal } from "../helpers/timezones";
import { Spacing } from "../types/enum";
import type { ZoneLabel } from "../types/graphql";
import CalendarZoneOutreach from "./CalendarZoneOutreach";
import MemberExperienceBar, { DEFAULT_PROFILE, type MemberProfile } from "./MemberExperienceBar";

interface CalendarEvent {
  id: string;
  title: string;
  startDate: string;
  zone: ZoneLabel;
  dayOfWeek: number;
  timeUtc: string;
  hostType: string;
  hostFirstName?: string | null;
  hostName?: string | null;
  kind: string;
  level: string;
  category?: string | null;
  visibility: string;
  maxParticipants?: number | null;
  registered: number;
  unlimited: boolean;
  isFull: boolean;
  recurring: boolean;
  isCoachEvent: boolean;
  needsThreeEvents: boolean;
  participants: string[];
}

const HOST_HEAT: Record<string, { bg: string; tx: string }> = {
  "Big Sister": { bg: "#5FAE8D", tx: "#0f2e20" },
  Coach: { bg: "#7FA8E0", tx: "#16315e" },
  CXT: { bg: "#C9BBE6", tx: "#3b2c5c" },
  Member: { bg: "#F4C77E", tx: "#7a430a" },
};
const HOST_ORDER = ["Big Sister", "Coach", "CXT", "Member"];
const DAY_NAMES = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const GAP_RED = "#A32D2D";

/** How a dot reads once a member is being simulated. */
const VERDICT_COLOUR: Record<string, string> = {
  OPEN: "", // keeps its host colour
  LOCKED: "#D36A19", // needs three events
  BLOCKED: "#D5D2CB", // refused outright
};

const JOIN_WORDS: Record<string, string> = {
  STAFF: "Staff — can attend everything",
  HOST_OR_COHOST: "She hosts this",
  INVITED: "She's invited — that overrides every other rule",
  UNVERIFIED_ALLOWED: "Unverified, but this event is open to unverified members",
  UNVERIFIED_BLOCKED: "She isn't verified and this event isn't open to unverified members",
  COACH_TIER_BLOCKED: "A coach event her plan doesn't include",
  KIND_ALLOWED: "Open to her member kind",
  KIND_BLOCKED: "Restricted to member kinds she isn't",
};

const LOCK_WORDS: Record<string, string> = {
  NEEDS_THREE_EVENTS: "Locked until she's attended 3 events",
  COACH_TIER_LOCKED: "Locked — a coach event her plan doesn't include",
};
const GAP_BG = "#FCEBEB";

const pad = (n: number) => String(n).padStart(2, "0");

/** Monday 00:00 UTC of the week containing `date`. */
const mondayOfUTC = (date: Date): Date => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow);
  return d;
};
const addDays = (d: Date, n: number): Date => {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + n);
  return x;
};
const fmtDay = (d: Date) =>
  `${d.getUTCDate()} ${d.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" })}`;

const zoneTimes = (zoneIndex: number): [string, string] => [
  `${pad(zoneIndex * 3)}:00`,
  `${pad(zoneIndex * 3 + 3)}:00`,
];

const APP_URL = import.meta.env.VITE_APP_URL;

const EventsCalendar: React.FC = () => {
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOfUTC(new Date()));
  const [visible, setVisible] = useState<Record<string, boolean>>({
    "Big Sister": true,
    Coach: true,
    CXT: true,
    Member: true,
  });
  const [gapsOnly, setGapsOnly] = useState(false);
  const [sel, setSel] = useState<string | null>(null);
  // Private events are off by default — they clutter the week and are rarely
  // what she's looking at.
  const [showPrivate, setShowPrivate] = useState(false);
  const [tz, setTz] = useState<string>(UTC_TZ);
  const [profile, setProfile] = useState<MemberProfile>(DEFAULT_PROFILE);
  const [simActive, setSimActive] = useState(false);
  const [loadedName, setLoadedName] = useState<string | null>(null);

  const { data, loading } = useQuery(ADMIN_EVENTS_CALENDAR, {
    variables: { weekStart: weekStart.toISOString() },
    fetchPolicy: "cache-and-network",
  });

  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);

  // What this member could attend, for exactly the week on screen.
  const { data: simData } = useQuery(MEMBER_EXPERIENCE, {
    variables: {
      from: weekStart.toISOString(),
      to: weekEnd.toISOString(),
      kind: profile.kind,
      isVerified: profile.isVerified,
      planSlug: profile.planSlug,
      attendedEvents: profile.attendedEvents,
      joinedAt: new Date(profile.joinedAt).toISOString(),
    },
    fetchPolicy: "network-only",
    skip: !simActive,
  });

  const sim = simData?.adminMemberExperience;
  const verdicts = useMemo(() => {
    const map = new Map<string, { verdict: string; joinReason: string; lockReason: string }>();
    for (const row of sim?.events ?? []) {
      map.set(row.eventId, row);
    }
    return map;
  }, [sim]);

  const events: CalendarEvent[] = data?.adminEventsCalendar?.events ?? [];

  // zone-dow -> events (filtered by which host types are shown)
  const grid = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of events) {
      if (!visible[ev.hostType]) {
        continue;
      }
      if (!showPrivate && ev.visibility === "Private") {
        continue;
      }
      // While a member is being simulated the calendar shows only what she could
      // actually attend. Hiding the rest is the point: an empty slot is then a
      // real gap for her, not a slot full of events she can't get into.
      if (simActive) {
        const verdict = verdicts.get(ev.id)?.verdict;
        if (verdict && verdict !== "OPEN") {
          continue;
        }
      }
      const key = `${ev.zone}-${ev.dayOfWeek}`;
      map.set(key, [...(map.get(key) ?? []), ev]);
    }
    return map;
  }, [events, visible, showPrivate, simActive, verdicts]);

  const shownTotal = useMemo(() => [...grid.values()].reduce((s, a) => s + a.length, 0), [grid]);
  const totalCells = ZONE_ORDER.length * 7;
  let gapCount = 0;
  for (const z of ZONE_ORDER) {
    for (let di = 0; di < DAY_NAMES.length; di++) {
      if (!(grid.get(`${z}-${di}`) ?? []).length) {
        gapCount++;
      }
    }
  }

  const selEvents = sel
    ? (grid.get(sel) ?? []).slice().sort((a, b) => a.timeUtc.localeCompare(b.timeUtc))
    : [];
  const selZoneIndex = sel ? ZONE_ORDER.indexOf(sel.split("-")[0] as ZoneLabel) : -1;
  const selDay = sel ? Number(sel.split("-")[1]) : -1;
  const selZone: ZoneLabel | null = sel ? (sel.split("-")[0] as ZoneLabel) : null;

  const toggleHost = (t: string) => {
    setVisible((v) => ({ ...v, [t]: !v[t] }));
    setSel(null);
  };
  const shiftWeek = (delta: number) => {
    setWeekStart((w) => addDays(w, delta * 7));
    setSel(null);
  };

  const weekLabel = `${fmtDay(weekStart)} – ${fmtDay(addDays(weekStart, 6))}`;
  const isThisWeek = weekStart.getTime() === mondayOfUTC(new Date()).getTime();

  return (
    <Box>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{
          background: "#f7f6f3",
          borderRadius: 1,
          p: 1.25,
          mb: 2,
          borderLeft: "2px solid #ccc9c0",
        }}
      >
        Each row is a 3-hour UTC colour zone; each column a day. Every square is one event, coloured
        by who's hosting. <strong style={{ color: GAP_RED }}>Red cells are coverage gaps.</strong> A{" "}
        <strong>❗</strong> means a full event. Click a slot to see its events and jump to their
        details, participants or edit.
      </Typography>

      <MemberExperienceBar
        profile={profile}
        onChange={setProfile}
        active={simActive}
        onActiveChange={setSimActive}
        loadedName={loadedName}
        onLoadedNameChange={setLoadedName}
        summary={
          sim
            ? {
                total: sim.total,
                canAttend: sim.canAttend,
                locked: sim.locked,
                blocked: sim.blocked,
              }
            : undefined
        }
      />

      {simActive && (
        <Box display="flex" alignItems="center" gap={Spacing.m} mb={Spacing.s} flexWrap="wrap">
          <Typography variant="caption" color="textSecondary">
            Showing only what she can attend — empty slots are her gaps.
          </Typography>
          {[
            ["Can attend", "", "host colour"],
            ["Locked — needs 3 events", VERDICT_COLOUR.LOCKED, ""],
            ["Can't attend", VERDICT_COLOUR.BLOCKED, ""],
          ].map(([label, colour, note]) => (
            <Box key={label} display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  bgcolor: colour || HOST_HEAT["Big Sister"].bg,
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)",
                }}
              />
              <Typography variant="caption" color="textSecondary">
                {label}
                {note ? ` (${note})` : ""}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Week nav + host toggles + gaps */}
      <Box display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap" mb={Spacing.s}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => shiftWeek(-1)}
            sx={{ minWidth: 34 }}
          >
            ‹
          </Button>
          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 150, textAlign: "center" }}>
            {weekLabel}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => shiftWeek(1)}
            sx={{ minWidth: 34 }}
          >
            ›
          </Button>
          {!isThisWeek && (
            <Button
              size="small"
              onClick={() => {
                setWeekStart(mondayOfUTC(new Date()));
                setSel(null);
              }}
            >
              Today
            </Button>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
          <Typography variant="caption" color="textSecondary">
            Show:
          </Typography>
          {HOST_ORDER.map((t) => {
            const on = visible[t];
            return (
              <Chip
                key={t}
                label={t}
                size="small"
                onClick={() => toggleHost(t)}
                sx={{
                  cursor: "pointer",
                  fontWeight: on ? 600 : 400,
                  bgcolor: on ? HOST_HEAT[t].bg : "transparent",
                  color: on ? HOST_HEAT[t].tx : "text.disabled",
                  border: `1px solid ${on ? HOST_HEAT[t].bg : "#e4e2dc"}`,
                }}
              />
            );
          })}
        </Box>

        <Chip
          label="Highlight gaps only"
          size="small"
          variant={gapsOnly ? "filled" : "outlined"}
          color={gapsOnly ? "error" : "default"}
          onClick={() => setGapsOnly((g) => !g)}
          sx={{ cursor: "pointer" }}
        />
        <Chip
          size="small"
          label={showPrivate ? "Private events shown" : "Private events hidden"}
          variant={showPrivate ? "filled" : "outlined"}
          onClick={() => setShowPrivate((p) => !p)}
        />

        <TextField
          select
          size="small"
          label="Show times in"
          value={tz}
          onChange={(event) => setTz(event.target.value)}
          sx={{ minWidth: 200 }}
        >
          {TIMEZONES.map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {tz !== UTC_TZ && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: "block", mb: Spacing.s }}
        >
          Event times shown in {tzLabel(tz)} (a member's local clock there). Zone rows stay fixed
          3-hour UTC bands.
        </Typography>
      )}

      <Box display="flex" gap={Spacing.m} flexWrap="wrap" mb={Spacing.s} alignItems="center">
        <Typography variant="caption" color="textSecondary">
          <strong>{shownTotal}</strong> events{loading ? " · loading…" : ""}
        </Typography>
        <Typography variant="caption" sx={{ color: gapCount ? GAP_RED : "success.main" }}>
          <strong>{gapCount}</strong> / {totalCells} slots have no event
        </Typography>
      </Box>

      {/* Grid */}
      <Box
        sx={{ border: "1px solid #e4e2dc", borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}
      >
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 660 }}>
            {/* header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "92px repeat(7, minmax(66px, 1fr))",
                bgcolor: "#f7f6f3",
                borderBottom: "1px solid #e4e2dc",
              }}
            >
              <Box
                sx={{
                  p: 1,
                  textAlign: "right",
                  fontSize: 10,
                  fontWeight: 600,
                  color: "#6b6860",
                  borderRight: "1px solid #e4e2dc",
                }}
              >
                UTC zone
              </Box>
              {DAY_NAMES.map((d, di) => (
                <Box
                  key={d}
                  sx={{
                    p: 1,
                    textAlign: "center",
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#6b6860",
                    letterSpacing: ".05em",
                    borderRight: "1px solid #e4e2dc",
                  }}
                >
                  {d}
                  <Box
                    component="span"
                    sx={{ display: "block", fontWeight: 400, color: "#a09d96" }}
                  >
                    {fmtDay(addDays(weekStart, di))}
                  </Box>
                </Box>
              ))}
            </Box>

            {/* zone rows */}
            {ZONE_ORDER.map((zone, zi) => {
              const [start, end] = zoneTimes(zi);
              const colour = ZONE_COLOURS.get(zone);
              return (
                <Box
                  key={zone}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "92px repeat(7, minmax(66px, 1fr))",
                    borderBottom: "1px solid #e4e2dc",
                  }}
                >
                  <Box
                    sx={{
                      p: 0.75,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      justifyContent: "center",
                      gap: 0.25,
                      borderRight: "1px solid #e4e2dc",
                      bgcolor: "#fafaf8",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 10, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}
                    >
                      {start}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        fontSize: 9,
                        px: 0.75,
                        borderRadius: 0.5,
                        bgcolor: colour,
                        color: "#fff",
                        fontWeight: 600,
                      }}
                    >
                      {zone}
                    </Box>
                    <Typography
                      sx={{ fontSize: 9, color: "#a09d96", fontVariantNumeric: "tabular-nums" }}
                    >
                      {end}
                    </Typography>
                  </Box>

                  {DAY_NAMES.map((_, di) => {
                    const key = `${zone}-${di}`;
                    const evs = (grid.get(key) ?? [])
                      .slice()
                      .sort(
                        (a, b) => HOST_ORDER.indexOf(a.hostType) - HOST_ORDER.indexOf(b.hostType),
                      );
                    const gap = evs.length === 0;
                    const dim = gapsOnly && !gap;
                    const isSel = sel === key;
                    const fullN = evs.filter((e) => e.isFull).length;
                    return (
                      <Box
                        key={key}
                        component="button"
                        onClick={() => (gap ? undefined : setSel(isSel ? null : key))}
                        sx={{
                          minHeight: 50,
                          cursor: gap ? "default" : "pointer",
                          bgcolor: gap && !dim ? GAP_BG : "#fff",
                          border: "none",
                          borderRight: "1px solid #e4e2dc",
                          outline: isSel ? "2px solid #00583D" : "none",
                          outlineOffset: -2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0.5,
                          fontFamily: "inherit",
                        }}
                      >
                        {gap ? (
                          <Box
                            component="span"
                            sx={{ fontSize: 11, color: dim ? "#e0ddd5" : GAP_RED }}
                          >
                            {gapsOnly ? "●" : "—"}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "2px",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: dim ? 0.25 : 1,
                            }}
                          >
                            {evs.map((e) => {
                              const v = verdicts.get(e.id);
                              const tint = v ? VERDICT_COLOUR[v.verdict] : "";
                              const why = v
                                ? v.verdict === "OPEN"
                                  ? `Can attend — ${JOIN_WORDS[v.joinReason] ?? ""}`
                                  : v.verdict === "LOCKED"
                                    ? (LOCK_WORDS[v.lockReason] ?? "Locked")
                                    : (JOIN_WORDS[v.joinReason] ?? "Can't attend")
                                : "";
                              const dot = (
                                <Box
                                  key={e.id}
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "2px",
                                    bgcolor: tint || (HOST_HEAT[e.hostType] ?? HOST_HEAT.Member).bg,
                                    boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)",
                                    position: "relative",
                                    opacity: v && v.verdict === "BLOCKED" ? 0.55 : 1,
                                  }}
                                >
                                  {e.recurring && (
                                    <Box
                                      component="span"
                                      sx={{
                                        position: "absolute",
                                        top: -6,
                                        right: -3,
                                        fontSize: 8,
                                        color: "#6b6860",
                                      }}
                                    >
                                      ↺
                                    </Box>
                                  )}
                                </Box>
                              );
                              return why ? (
                                <Tooltip key={e.id} title={`${e.title} — ${why}`}>
                                  {dot}
                                </Tooltip>
                              ) : (
                                dot
                              );
                            })}
                            {fullN > 0 && (
                              <Box component="span" sx={{ fontSize: 11, lineHeight: 1 }}>
                                ❗
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Detail panel */}
      {sel && selEvents.length > 0 && (
        <Box sx={{ mt: 2, border: "1px solid #e4e2dc", borderRadius: 2, p: 2, bgcolor: "#fff" }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1.5}
            flexWrap="wrap"
            gap={1}
          >
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                size="small"
                label={
                  tz === UTC_TZ
                    ? `${sel.split("-")[0]} · ${zoneTimes(selZoneIndex)[0]}–${zoneTimes(selZoneIndex)[1]} UTC`
                    : `${sel.split("-")[0]} · ${zoneLocal(zoneTimes(selZoneIndex)[0], tz)}–${zoneLocal(zoneTimes(selZoneIndex)[1], tz)} ${tzLabel(tz)}`
                }
                sx={{
                  bgcolor: ZONE_COLOURS.get(sel.split("-")[0] as ZoneLabel),
                  color: "#fff",
                  fontWeight: 600,
                }}
              />
              <Typography variant="body2" fontWeight={600}>
                {
                  ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][
                    selDay
                  ]
                }{" "}
                · {selEvents.length} event{selEvents.length > 1 ? "s" : ""}
              </Typography>
            </Box>
            <Button size="small" onClick={() => setSel(null)}>
              Close
            </Button>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            {selEvents.map((ev) => {
              const heat = HOST_HEAT[ev.hostType] ?? HOST_HEAT.Member;
              const cap = ev.unlimited
                ? "Unlimited"
                : `${ev.registered}/${ev.maxParticipants}${ev.isFull ? " · Full" : ""}`;
              // When a member is being simulated, every event says where it
              // stands for her — a dot can only carry a colour, and "why not"
              // is the part worth reading.
              const v = verdicts.get(ev.id);
              const verdictChip = v
                ? {
                    OPEN: {
                      label: "Can attend",
                      bg: "#E3F0EA",
                      tx: "#00583D",
                      why: JOIN_WORDS[v.joinReason] ?? "",
                    },
                    LOCKED: {
                      label: "🔒 Locked",
                      bg: "#FBEAD9",
                      tx: "#8A4310",
                      why: LOCK_WORDS[v.lockReason] ?? "Locked",
                    },
                    BLOCKED: {
                      label: "Can't attend",
                      bg: "#EFEDE8",
                      tx: "#6B6860",
                      why: JOIN_WORDS[v.joinReason] ?? "",
                    },
                  }[v.verdict]
                : null;
              return (
                <Box
                  key={ev.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: "#fafaf8",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      minWidth: tz === UTC_TZ ? 52 : 100,
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 13,
                    }}
                  >
                    {evLocal(ev.timeUtc, tz)}
                    {tz !== UTC_TZ && (
                      <Box
                        component="span"
                        sx={{ color: "text.disabled", fontWeight: 400, fontSize: 11 }}
                      >
                        {" "}
                        · {ev.timeUtc} UTC
                      </Box>
                    )}
                  </Typography>
                  <Chip
                    size="small"
                    label={ev.hostType}
                    sx={{ bgcolor: heat.bg, color: heat.tx, fontWeight: 600, height: 20 }}
                  />
                  {ev.isCoachEvent && (
                    <Tooltip title="Only members whose plan includes coaching can attend">
                      <Chip
                        size="small"
                        label="Coaching only"
                        sx={{ bgcolor: "#E8EEF8", color: "#16315e", fontWeight: 600, height: 20 }}
                      />
                    </Tooltip>
                  )}
                  {ev.needsThreeEvents && (
                    <Tooltip title="Shows locked until she has attended three events">
                      <Chip
                        size="small"
                        label="3+ events"
                        sx={{ bgcolor: "#FBEAD9", color: "#8A4310", fontWeight: 600, height: 20 }}
                      />
                    </Tooltip>
                  )}
                  {verdictChip && (
                    <Tooltip title={verdictChip.why}>
                      <Chip
                        size="small"
                        label={verdictChip.label}
                        sx={{
                          bgcolor: verdictChip.bg,
                          color: verdictChip.tx,
                          fontWeight: 600,
                          height: 20,
                        }}
                      />
                    </Tooltip>
                  )}
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#00583D" }}>
                      {ev.title}{" "}
                      {ev.recurring && (
                        <Box component="span" title="Repeats weekly">
                          ↺
                        </Box>
                      )}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {ev.hostFirstName ?? ev.hostName ?? "—"} · {ev.kind} · {ev.level} ·{" "}
                      {ev.visibility}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={cap}
                    color={ev.isFull ? "error" : "default"}
                    variant={ev.isFull ? "filled" : "outlined"}
                    sx={{ height: 20 }}
                  />
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/event/${ev.id}/details`}
                    >
                      Details
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/event/${ev.id}/participants`}
                    >
                      Participants
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={`/event/${ev.id}/edit`}
                    >
                      Edit
                    </Button>
                    {APP_URL && (
                      <Button
                        size="small"
                        href={`${APP_URL}/event/${ev.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open ↗
                      </Button>
                    )}
                  </Box>
                  {selZone && (
                    <CalendarZoneOutreach
                      eventId={ev.id}
                      eventTitle={ev.title}
                      zone={selZone}
                      tz={tz}
                      cohortKind={simActive ? profile.kind : undefined}
                      cohortPlan={simActive ? profile.planSlug : undefined}
                    />
                  )}
                </Box>
              );
            })}
            {selEvents.some((e) => e.participants.length > 0) && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                Signed up:{" "}
                {selEvents
                  .flatMap((e) => e.participants)
                  .slice(0, 12)
                  .join(", ")}
                {selEvents.flatMap((e) => e.participants).length > 12 ? "…" : ""}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Legend */}
      <Box display="flex" gap={Spacing.m} flexWrap="wrap" alignItems="center" mt={Spacing.m}>
        {HOST_ORDER.map((t) => (
          <Box key={t} display="flex" alignItems="center" gap={0.75}>
            <Box sx={{ width: 11, height: 11, borderRadius: "3px", bgcolor: HOST_HEAT[t].bg }} />
            <Typography variant="caption" color="textSecondary">
              {t}
            </Typography>
          </Box>
        ))}
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box
            sx={{
              width: 11,
              height: 11,
              borderRadius: "3px",
              bgcolor: GAP_BG,
              border: "1px solid #f0c9c9",
            }}
          />
          <Typography variant="caption" color="textSecondary">
            Coverage gap
          </Typography>
        </Box>
        <Typography variant="caption" color="textSecondary">
          ↺ repeats weekly · ❗ full event
        </Typography>
      </Box>

      {loading && !data && (
        <Box display="flex" justifyContent="center" p={Spacing.xl}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default EventsCalendar;
